import { useEffect, useMemo, useState } from 'react';
import { Api } from '../../application/panelApi';
import { useApp } from '../../store/AppContext';
import type { ClusterProfileStats, NodeCluster } from './model';

export type OverviewRow = { c: NodeCluster; prof: ClusterProfileStats; stale: boolean };

export type OverviewTotals = {
  total: number;
  active: number;
  ready: number;
  disk: number;
  diskTotal: number;
};






export function useProfilesOverview(cid: string): { rows: OverviewRow[] | null; version: number; clusters: NodeCluster[] } {
  const { clusters: rawClusters, controlMode } = useApp();
  const clusters = useMemo(() => rawClusters as NodeCluster[], [rawClusters]);
  const [rows, setRows] = useState<OverviewRow[] | null>(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (controlMode !== 'control' || !clusters.length) {
      setRows(null);
      return undefined;
    }
    let alive = true;
    const targets = cid === 'all' ? clusters : clusters.filter((c) => c.id === cid);
    void Promise.all(
      targets.map(async (c): Promise<OverviewRow> => {
        try {
          const data = (await Api.clusters.profiles(c.id)) as { profiles?: ClusterProfileStats };
          return { c, prof: data.profiles ?? {}, stale: false };
        } catch {
          const live = c.live;
          const prof: ClusterProfileStats = live
            ? {
                total: live.profilesTotal,
                active: live.profilesActive,
                idle: live.profilesIdle,
                ready: live.profilesReady,
                diskMb: live.profilesDiskMb,
                diskTotalMb: live.profilesDiskTotalMb,
              }
            : { total: 0, active: 0, idle: 0, ready: 0, diskMb: null, diskTotalMb: null };
          return { c, prof, stale: true };
        }
      }),
    ).then((r) => {
      if (!alive) return;
      setRows(r);
      setVersion((v) => v + 1);
    });
    return () => {
      alive = false;
    };
  }, [cid, clusters, controlMode]);

  return { rows, version, clusters };
}

export function overviewTotals(rows: OverviewRow[]): OverviewTotals {
  return rows.reduce<OverviewTotals>(
    (a, { prof }) => ({
      total: a.total + (prof.total || 0),
      active: a.active + (prof.active || 0),
      ready: a.ready + (prof.ready || 0),
      disk: a.disk + (prof.diskMb || 0),
      diskTotal: a.diskTotal + (prof.diskTotalMb || 0),
    }),
    { total: 0, active: 0, ready: 0, disk: 0, diskTotal: 0 },
  );
}